import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@mxa-argos/components/shadcn/command';
import { SearchItemContent } from '@mxa-argos/modules/search/components/search-item-content';
import {
  routeByResourceType,
  type SearchResult,
  useResourceSearch,
} from '@mxa-argos/modules/search/hooks/use-resource-search';
import { Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

const isMac = () => navigator.platform?.toUpperCase().includes('MAC') ?? false;

export const CommandSearch: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const modifierKey = useMemo(() => (isMac() ? '⌘' : 'Ctrl+'), []);

  const {
    searchQuery,
    loading,
    hasResults,
    isQueryTooShort,
    groupEntries,
    handleValueChange,
    reset,
  } = useResourceSearch();

  const searchInputRef = useCallback((node: HTMLInputElement | null) => {
    if (node) {
      node.setAttribute('type', 'search');
      node.setAttribute('readonly', 'readonly');
    }
  }, []);

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.removeAttribute('readonly');
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSelect = (result: SearchResult) => {
    const route = routeByResourceType[result.type];
    startTransition(() => {
      void navigate(`${route}/${result.id}`);
      setOpen(false);
      reset();
    });
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      reset();
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={t('search.placeholder')}
        className="inline-flex w-[28rem] items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        <Search className="h-4 w-4" />
        <span className="hidden flex-1 text-left sm:inline-flex">{t('search.placeholder')}</span>
        <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium sm:flex">
          <span className="text-xs">{modifierKey}</span>K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={handleOpenChange} shouldFilter={false}>
        <CommandInput
          ref={searchInputRef}
          placeholder={t('search.inputPlaceholder')}
          value={searchQuery}
          onValueChange={handleValueChange}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          onFocus={handleInputFocus}
        />
        <CommandList>
          {isPending && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('search.loading')}
            </div>
          )}

          {!isPending && loading && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('search.searching')}
            </div>
          )}

          {!isPending && !loading && isQueryTooShort && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t('search.keepTyping')}
            </div>
          )}

          {!isPending && !loading && searchQuery && !isQueryTooShort && !hasResults && (
            <CommandEmpty>{t('search.noResults')}</CommandEmpty>
          )}

          {!isPending &&
            !loading &&
            groupEntries.map(([type, items], idx) => (
              <div key={type}>
                {idx > 0 && <CommandSeparator />}
                <CommandGroup heading={t(`search.resourceTypes.${type}`)}>
                  {items?.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={`${item.type}-${item.id}-${item.primary}`}
                      onSelect={() => handleSelect(item)}
                      className="flex-col items-start"
                    >
                      <SearchItemContent result={item} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </div>
            ))}

          {!isPending && !searchQuery && !loading && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t('search.startTyping')}
            </div>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
};
